/* ==========================================================================
   AUREN — product.js
   Variant engine · Gallery + zoom · 360 spin · Sticky ATC · Bundle totals
   ========================================================================== */
(function () {
  'use strict';

  const U = window.AURENUtils;
  const S = window.AUREN.strings;

  /* ------------------------------------------------------------------ *
   * Variant selection
   * ------------------------------------------------------------------ */
  class AurenVariants extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-variant-json]');
      if (!dataEl) return;
      this.variants = JSON.parse(dataEl.textContent);
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.updateUrl = this.dataset.updateUrl !== 'false';
      this.addEventListener('change', () => this.onChange());
      this.onChange(true);
    }

    get selectedOptions() {
      const fromRadios = U.qsa('input[type="radio"]:checked', this).map((i) => i.value);
      if (fromRadios.length) return fromRadios;
      return U.qsa('select', this).map((s) => s.value);
    }

    onChange(initial) {
      const options = this.selectedOptions;
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === options[i]));

      this.updateOptionLabels(options);
      this.updateAvailability(options);

      const idInput = document.querySelector(`[data-variant-id-input="${this.sectionId}"]`);
      if (idInput) idInput.value = variant ? variant.id : '';

      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (!initial && this.updateUrl) {
        window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
      }

      this.renderPrice(variant);
      this.renderButton(variant);
      this.renderInventory(variant);
      this.renderSku(variant);
      if (variant.featured_media) this.selectMedia(variant.featured_media.id);

      U.emit('auren:variant:change', { variant, sectionId: this.sectionId });
    }

    updateOptionLabels(options) {
      U.qsa('[data-option-display]', this).forEach((el, i) => {
        if (options[i]) el.textContent = options[i];
      });
    }

    /* Disable combinations that do not exist — reduces choice paralysis (Hick's Law) */
    updateAvailability(options) {
      const groups = U.qsa('.variant-group', this);
      groups.forEach((group, index) => {
        U.qsa('input[type="radio"]', group).forEach((input) => {
          const test = options.slice();
          test[index] = input.value;
          const match = this.variants.find((v) => v.options.every((opt, i) => (i === index ? opt === test[i] : opt === options[i])));
          const available = match && match.available;
          input.disabled = !match;
          input.closest('.variant-option, .swatch')?.classList.toggle('is-unavailable', !available);
        });
      });
    }

    renderPrice(variant) {
      const host = document.querySelector(`[data-price-host="${this.sectionId}"]`);
      if (!host) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      host.classList.toggle('price--on-sale', !!onSale);
      const regular = host.querySelector('[data-price-regular]');
      const compare = host.querySelector('[data-price-compare]');
      const save = host.querySelector('[data-price-save]');
      if (regular) regular.textContent = U.formatMoney(variant.price);
      if (compare) {
        compare.textContent = onSale ? U.formatMoney(variant.compare_at_price) : '';
        compare.hidden = !onSale;
      }
      if (save) {
        const amount = onSale ? variant.compare_at_price - variant.price : 0;
        save.textContent = onSale ? `Save ${U.formatMoney(amount)}` : '';
        save.hidden = !onSale;
      }
      U.qsa(`[data-sticky-price="${this.sectionId}"]`).forEach((el) => (el.textContent = U.formatMoney(variant.price)));
    }

    renderButton(variant) {
      U.qsa(`[data-add-button="${this.sectionId}"]`).forEach((btn) => {
        const label = btn.querySelector('[data-add-label]') || btn;
        btn.disabled = !variant.available;
        btn.classList.toggle('is-disabled', !variant.available);
        label.textContent = variant.available ? S.addToCart : S.soldOut;
      });
      const dynamic = document.querySelector(`[data-dynamic-checkout="${this.sectionId}"]`);
      if (dynamic) dynamic.hidden = !variant.available;
    }

    setUnavailable() {
      U.qsa(`[data-add-button="${this.sectionId}"]`).forEach((btn) => {
        const label = btn.querySelector('[data-add-label]') || btn;
        btn.disabled = true;
        btn.classList.add('is-disabled');
        label.textContent = S.unavailable;
      });
    }

    renderInventory(variant) {
      const host = document.querySelector(`[data-inventory-host="${this.sectionId}"]`);
      if (!host) return;
      const qty = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : Infinity;
      const policy = variant.inventory_policy;
      const management = variant.inventory_management;
      const threshold = Number(host.dataset.threshold || 12);
      const fill = host.querySelector('.stock-meter__fill');
      const label = host.querySelector('[data-inventory-label]');
      if (!variant.available) {
        host.hidden = true;
        return;
      }
      host.hidden = false;
      if (!management || (policy === 'continue' && qty <= 0)) {
        if (fill) fill.style.width = '100%';
        if (label) {
          label.textContent = 'In stock';
          label.className = 'stock-meter__label stock-meter__label--in';
        }
        return;
      }
      const pct = Math.max(8, Math.min(100, (qty / (threshold * 2)) * 100));
      if (fill) fill.style.width = `${pct}%`;
      if (label) {
        label.className = 'stock-meter__label' + (qty > threshold ? ' stock-meter__label--in' : '');
        label.textContent = qty > threshold ? 'In stock' : `Only ${qty} left in stock`;
      }
    }

    renderSku(variant) {
      const sku = document.querySelector(`[data-sku="${this.sectionId}"]`);
      if (sku) sku.textContent = variant.sku || '';
    }

    selectMedia(mediaId) {
      const gallery = document.querySelector(`[data-gallery="${this.sectionId}"]`);
      if (gallery && typeof gallery.goToMedia === 'function') gallery.goToMedia(mediaId);
    }
  }
  customElements.define('auren-variants', AurenVariants);

  /* ------------------------------------------------------------------ *
   * Gallery
   * ------------------------------------------------------------------ */
  class AurenGallery extends HTMLElement {
    connectedCallback() {
      this.slidesWrap = this.querySelector('.gallery__slides');
      this.slides = U.qsa('.gallery__slide', this);
      this.thumbs = U.qsa('.gallery__thumb', this);
      this.counter = this.querySelector('[data-gallery-counter]');
      this.zoomEnabled = this.dataset.zoom === 'true';

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goToMedia(thumb.dataset.mediaId));
      });

      if (this.slidesWrap) {
        this.slidesWrap.addEventListener('scroll', U.throttle(() => this.syncActive(), 110), { passive: true });
      }

      if (this.zoomEnabled) {
        this.slides.forEach((slide) => {
          const img = slide.querySelector('img');
          if (!img) return;
          slide.style.cursor = 'zoom-in';
          slide.addEventListener('click', () => this.openLightbox(slide.dataset.mediaId));
          // Desktop hover pan-zoom
          slide.addEventListener('mousemove', (e) => {
            if (!window.matchMedia('(min-width: 990px)').matches) return;
            const rect = slide.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;
            img.style.transformOrigin = `${x}% ${y}%`;
          });
        });
      }

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.step(1);
        if (e.key === 'ArrowLeft') this.step(-1);
      });

      this.syncActive();
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index === -1 || !this.slidesWrap) return;
      this.slidesWrap.scrollTo({ left: this.slides[index].offsetLeft - this.slidesWrap.offsetLeft, behavior: 'smooth' });
      this.setActive(index);
    }

    step(dir) {
      const current = this.activeIndex || 0;
      const next = Math.max(0, Math.min(this.slides.length - 1, current + dir));
      this.goToMedia(this.slides[next].dataset.mediaId);
    }

    syncActive() {
      if (!this.slidesWrap) return;
      const index = Math.round(this.slidesWrap.scrollLeft / (this.slidesWrap.clientWidth || 1));
      this.setActive(index);
    }

    setActive(index) {
      this.activeIndex = index;
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
    }

    openLightbox(mediaId) {
      const modal = document.getElementById('MediaLightbox');
      if (!modal) return;
      const stage = modal.querySelector('[data-lightbox-stage]');
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      const img = slide && slide.querySelector('img');
      if (stage && img) {
        stage.innerHTML = `<img src="${img.dataset.zoomSrc || img.currentSrc || img.src}" alt="${img.alt}" style="width:100%;height:auto;border-radius:var(--r-md)">`;
      }
      modal.open();
    }
  }
  customElements.define('auren-gallery', AurenGallery);

  /* ------------------------------------------------------------------ *
   * 360° spinner — drag through a frame sequence
   * ------------------------------------------------------------------ */
  class AurenSpin360 extends HTMLElement {
    connectedCallback() {
      this.frames = U.qsa('img', this);
      if (this.frames.length < 2) return;
      this.index = 0;
      this.dragging = false;
      this.startX = 0;
      this.show(0);

      const onDown = (x) => {
        this.dragging = true;
        this.startX = x;
      };
      const onMove = (x) => {
        if (!this.dragging) return;
        const delta = x - this.startX;
        if (Math.abs(delta) < 6) return;
        this.startX = x;
        this.show((this.index + (delta > 0 ? 1 : -1) + this.frames.length) % this.frames.length);
      };
      const onUp = () => (this.dragging = false);

      this.addEventListener('mousedown', (e) => { e.preventDefault(); onDown(e.clientX); });
      window.addEventListener('mousemove', (e) => onMove(e.clientX));
      window.addEventListener('mouseup', onUp);
      this.addEventListener('touchstart', (e) => onDown(e.touches[0].clientX), { passive: true });
      this.addEventListener('touchmove', (e) => onMove(e.touches[0].clientX), { passive: true });
      this.addEventListener('touchend', onUp);
    }
    show(i) {
      this.index = i;
      this.frames.forEach((f, n) => (f.hidden = n !== i));
    }
  }
  customElements.define('auren-spin-360', AurenSpin360);

  /* ------------------------------------------------------------------ *
   * Sticky add-to-cart bar
   * ------------------------------------------------------------------ */
  function initStickyBar() {
    const bar = document.querySelector('[data-sticky-atc]');
    const anchor = document.querySelector('[data-sticky-anchor]');
    if (!bar || !anchor) return;
    const io = new IntersectionObserver(
      ([entry]) => bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 }
    );
    io.observe(anchor);
  }

  /* ------------------------------------------------------------------ *
   * Frequently bought together total
   * ------------------------------------------------------------------ */
  function initBundle() {
    const bundle = document.querySelector('[data-bundle]');
    if (!bundle) return;
    const totalEl = bundle.querySelector('[data-bundle-total]');
    function update() {
      let total = Number(bundle.dataset.basePrice || 0);
      bundle.querySelectorAll('[data-bundle-item]:checked').forEach((i) => (total += Number(i.dataset.price || 0)));
      if (totalEl) totalEl.textContent = U.formatMoney(total);
    }
    bundle.addEventListener('change', update);
    document.addEventListener('auren:variant:change', (e) => {
      bundle.dataset.basePrice = e.detail.variant.price;
      update();
    });
    update();
  }

  function boot() {
    initStickyBar();
    initBundle();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
